import { omit, isEqual } from 'lodash/fp';
import { TRAINING_DEFAULT_VIEW } from 'constants/actionTypes';
import { filterUpdateAll } from 'actions/shared/index/filter';
import { initialState as filterInitialState } from 'reducers/eventsIndex/filters';

const DEFAULT_VIEW_STORAGE_KEY = 'defaultEventsView.filters';

export const VIEW_TYPES = {
  DIRTY: 'dirty',
  SET: 'set',
  UNSET: 'unset',
};

const setDefaultViewState = view => ({
  type: TRAINING_DEFAULT_VIEW.SET_VIEW_STATE,
  view,
});

/**
 * Exclude all keys listed below from the object it's called with
 */
const getPersistedFilterParams = omit(['perPage', 'isActive', 'page', 'url']);

/**
 * Check if the filter does not match the initial state of the filter reducer
 */
const isApplyingCustomFilters = filter =>
  !isEqual(
    getPersistedFilterParams(filter),
    getPersistedFilterParams(filterInitialState),
  );

/**
 * Return the saved filter object from localStorage if it exists.
 * Return Null otherwise.
 */
const getStoredFilters = () => {
  const storedFilters = localStorage.getItem(DEFAULT_VIEW_STORAGE_KEY);
  return storedFilters ? JSON.parse(storedFilters) : null;
};

/**
 * An action that applies restore the default view by applying the persisted
 * filters. See: filterUpdateAll
 */
export const loadDefaultView = () => (dispatch, getState) =>
  new Promise(resolve => {
    const storedFilter = getStoredFilters();
    dispatch(filterUpdateAll(storedFilter));
    // default view may be set by the time this action is called.
    // See initDefaultView
    if (getState().defaultView !== VIEW_TYPES.SET) {
      dispatch(setDefaultViewState(VIEW_TYPES.SET));
    }
    resolve();
  });

/**
 * An action that saves the current applied filters as the default view by
 * storing them in localStorage, and updating the defaultView state accordingly
 */
export const saveDefaultView = () => (dispatch, getState) =>
  new Promise(resolve => {
    const filters = getPersistedFilterParams(getState().filter);
    localStorage.setItem(DEFAULT_VIEW_STORAGE_KEY, JSON.stringify(filters));
    dispatch(setDefaultViewState(VIEW_TYPES.SET));
    resolve();
  });

/**
 * An action that determines the state of the current view. State can be the
 * value of one of the VIEW_TYPES keys
 */
export const updateDefaultViewState = () => (dispatch, getState) => {
  const currentFilter = getPersistedFilterParams(getState().filter);
  const storedFilter = getStoredFilters();
  if (!storedFilter) {
    dispatch(setDefaultViewState(VIEW_TYPES.UNSET));
  } else if (isEqual(currentFilter, storedFilter)) {
    dispatch(setDefaultViewState(VIEW_TYPES.SET));
  } else {
    dispatch(setDefaultViewState(VIEW_TYPES.DIRTY));
  }
};

/**
 * An action to be dispatched on page load. This will handle restoring the
 * default on page load if A) it exists and B) the page is not applying custom
 * filters.
 */
export const initDefaultView = () => (dispatch, getState) => {
  // determine the initial state of the default view (one of VIEW_TYPES)
  dispatch(updateDefaultViewState());
  if (
    // skip if the default view isn't set (e.g. first time users).
    getState().defaultView !== VIEW_TYPES.UNSET &&
    // skip if the page is already applying custom filters (e.g. via url params)
    !isApplyingCustomFilters(getState().filter)
  ) {
    // otherwise, load the default view
    dispatch(loadDefaultView());
  }
};
